import { SurveyModel } from 'survey-core';
import {
  extractTimeFromDateTime,
  parseSeriesDateEntries,
  toDateOnly,
} from './event-schedule-form.util';

type SeriesDateRow = {
  date: string;
  series_date_guid?: string;
};

function parseDayCateringJson(data: Record<string, unknown>): Record<string, string> {
  const raw = data['series_day_catering'];
  if (typeof raw !== 'string' || !raw.trim()) {
    return {};
  }
  try {
    const parsed = JSON.parse(raw) as Record<string, string>;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function parseDayLocationsJson(
  data: Record<string, unknown>
): Record<string, Record<string, string>> {
  const raw = data['series_day_locations'];
  if (typeof raw !== 'string' || !raw.trim()) {
    return {};
  }
  try {
    const parsed = JSON.parse(raw) as Record<string, Record<string, string>>;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function readLocationDefaults(data: Record<string, unknown>): {
  location: string;
  room: string;
  manual_location: string;
  manual_location_address: string;
  manual_room_capacity: string;
} {
  return {
    location: String(data['series_default_location'] || data['location'] || ''),
    room: String(data['series_default_room'] || data['room'] || ''),
    manual_location: String(
      data['series_default_manual_location'] || data['manual_location'] || ''
    ),
    manual_location_address: String(
      data['series_default_manual_location_address'] ||
        data['manual_location_address'] ||
        ''
    ),
    manual_room_capacity: String(
      data['series_default_manual_room_capacity'] ||
        data['manual_room_capacity'] ||
        ''
    ),
  };
}

/** Period → hidden series fields + planner (when entering multi-date view). */
export function syncPeriodScheduleToSeriesFields(sender: SurveyModel): void {
  const data = (sender.data || {}) as Record<string, unknown>;

  const dates: SeriesDateRow[] = [];
  const start = String(data['start_date'] || '');
  const startDay = toDateOnly(start);
  if (startDay) {
    dates.push({ date: startDay });
  }

  const additional = (data['additional_dates'] as Array<{ sub_event_date?: string }>) || [];
  for (const row of additional) {
    const day = toDateOnly(row.sub_event_date);
    if (day && !dates.some((d) => d.date === day)) {
      dates.push({ date: day });
    }
  }

  const existing = parseSeriesDateEntries(data);
  const merged = new Map<string, string>();
  for (const entry of existing) {
    if (entry.date) {
      merged.set(entry.date, entry.date);
    }
  }
  for (const row of dates) {
    merged.set(row.date, row.date);
  }
  const mergedDates = Array.from(merged.values()).sort((a, b) =>
    a.localeCompare(b)
  );
  if (mergedDates.length === 0) {
    return;
  }

  sender.setValue(
    'series_dates',
    JSON.stringify(mergedDates.map((date) => ({ date })))
  );
  sender.setValue('series_planner_active', 'true');

  const startTime = extractTimeFromDateTime(start) || '12:00';
  sender.setValue('series_default_start_time', startTime.slice(0, 5));
  const end = String(data['end_date'] || '');
  const endTime = extractTimeFromDateTime(end) || '13:00';
  sender.setValue('series_default_end_time', endTime.slice(0, 5));

  const loc = readLocationDefaults(data);
  sender.setValue('series_default_location', loc.location);
  sender.setValue('series_default_room', loc.room);
  sender.setValue('series_default_manual_location', loc.manual_location);
  sender.setValue('series_default_manual_location_address', loc.manual_location_address);
  sender.setValue('series_default_manual_room_capacity', loc.manual_room_capacity);
  sender.setValue(
    'series_default_catering',
    String(data['catering'] || 'irrelevant')
  );
}

/** Series / hidden fields → period form fields (when entering period view). */
export function syncSeriesFieldsToPeriodForm(sender: SurveyModel): void {
  const data = (sender.data || {}) as Record<string, unknown>;
  const entries = parseSeriesDateEntries(data);
  if (!entries.length) {
    return;
  }

  const sorted = [...entries].sort((a, b) => a.date.localeCompare(b.date));
  const first = sorted[0];

  const defaultStart = String(data['series_default_start_time'] || '12:00').slice(0, 5);
  const defaultEnd = String(data['series_default_end_time'] || '13:00').slice(0, 5);

  let dayTimes: Record<string, { start?: string; end?: string }> = {};
  const rawTimes = data['series_day_times'];
  if (typeof rawTimes === 'string' && rawTimes.trim()) {
    try {
      dayTimes = JSON.parse(rawTimes);
    } catch {
      dayTimes = {};
    }
  }

  const dayLocs = parseDayLocationsJson(data);
  const dayCatering = parseDayCateringJson(data);
  const defaultCatering = String(
    data['series_default_catering'] || data['catering'] || 'irrelevant'
  );
  const loc = readLocationDefaults(data);

  const firstStart = dayTimes[first.date]?.start?.slice(0, 5) || defaultStart;
  const firstEnd = dayTimes[first.date]?.end?.slice(0, 5) || defaultEnd;

  sender.setValue('start_date', `${first.date}T${firstStart}`);
  sender.setValue('end_date', `${first.date}T${firstEnd}`);

  const firstDayLoc = dayLocs[first.date];
  const firstLoc =
    firstDayLoc?.['location']
      ? {
          location: String(firstDayLoc['location'] || ''),
          room: String(firstDayLoc['room'] || ''),
          manual_location: String(firstDayLoc['manual_location'] || ''),
          manual_location_address: String(
            firstDayLoc['manual_location_address'] || ''
          ),
          manual_room_capacity: String(
            firstDayLoc['manual_room_capacity'] || ''
          ),
        }
      : loc;

  if (firstLoc.location) {
    sender.setValue('location', firstLoc.location);
    sender.setValue('room', firstLoc.room);
    sender.setValue('manual_location', firstLoc.manual_location);
    sender.setValue('manual_location_address', firstLoc.manual_location_address);
    sender.setValue('manual_room_capacity', firstLoc.manual_room_capacity);
  }

  const firstCatering = dayCatering[first.date] || defaultCatering;
  sender.setValue('catering', firstCatering);

  const additional: Record<string, unknown>[] = [];

  for (const entry of sorted.slice(1)) {
    const dayLoc = dayLocs[entry.date];
    const dayCat = dayCatering[entry.date];
    const row: Record<string, unknown> = {
      sub_event_date: entry.date,
      sub_event_start_time: dayTimes[entry.date]?.start?.slice(0, 5) || defaultStart,
      sub_event_end_time: dayTimes[entry.date]?.end?.slice(0, 5) || defaultEnd,
      same_as_previous: false,
      same_catering_as_previous: false,
    };
    if (dayLoc?.['location']) {
      row['sub_event_location'] = dayLoc['location'];
      row['sub_event_room'] = dayLoc['room'] || '';
      row['sub_event_manual_location'] = dayLoc['manual_location'] || '';
      row['sub_event_manual_location_address'] =
        dayLoc['manual_location_address'] || '';
      row['sub_event_manual_room_capacity'] =
        dayLoc['manual_room_capacity'] || '';
    } else if (loc.location) {
      row['sub_event_location'] = loc.location;
      row['sub_event_room'] = loc.room;
      row['sub_event_manual_location'] = loc.manual_location;
      row['sub_event_manual_location_address'] = loc.manual_location_address;
      row['sub_event_manual_room_capacity'] = loc.manual_room_capacity;
      row['same_as_previous'] = true;
    }
    if (dayCat) {
      row['sub_event_catering'] = dayCat;
    } else if (defaultCatering) {
      row['sub_event_catering'] = defaultCatering;
      row['same_catering_as_previous'] = true;
    }
    additional.push(row);
  }

  sender.setValue('additional_dates', additional);
  sender.setValue('series_planner_active', 'false');
}
